import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useAtomValue, useSetAtom } from 'jotai';
import { BusinessProfile, Document_Type_Enum } from '@kheops/graphql';
import { OrderByReferenceIdDocument } from '../queries/__generated__/orderByReferenceId.generated';
import { UpsertDocumentOfOrderMutation, useUpsertDocumentOfOrderMutation } from '../mutations/__generated__/upsertDocumentOfOrder.generated';
import { currentContextAtom } from '../state';
import { commonSnackbarPropsAtom } from '../common/state/state';
import { FileTypes, useUploadFile } from './useUploadFile';

interface UseUploadDocumentProps {
  orderReferenceId: string;
  orderId: string;
  documentType: Document_Type_Enum;
}
interface UseUploadDocument {
  handleFileChange: (file: File, type: FileTypes, pathId: string) => Promise<void>;
  isDocumentUploadLoading: boolean;
  documentData: UpsertDocumentOfOrderMutation | null | undefined;
  reset: () => void;
}

export function useUploadDocument({ orderReferenceId, orderId, documentType }: UseUploadDocumentProps): UseUploadDocument {
  const setCommonSnackbarProps = useSetAtom(commonSnackbarPropsAtom);
  const { t } = useTranslation(['error']);
  const {
    handleFileChange: handleUploadFile,
    isFileUploadLoading,
    // here we need USER_INVOICE type to get the correct S3 bucket, since user generated invocies are stored in
    // a different bucket than invoices generated by Kheops
  } = useUploadFile();
  const [upsertDocumentOfOrder, { data, loading: isUpsertDocumentLoading, client, reset }] = useUpsertDocumentOfOrderMutation(
    {
      refetchQueries: [OrderByReferenceIdDocument],
      awaitRefetchQueries: true,
      onError: () => {
        setCommonSnackbarProps({
          label: t('error:order_has_already_been_updated'),
          snackbarProps: {
            open: true,
          },
          alertProps: {
            severity: 'error',
          },
        });
        client.refetchQueries({ include: [OrderByReferenceIdDocument] });
      },
    },
  );
  const isDocumentUploadLoading = useMemo(() => isFileUploadLoading || isUpsertDocumentLoading, [isUpsertDocumentLoading, isFileUploadLoading]);
  const { realm } = useAtomValue(currentContextAtom);

  const handleFileChange = async (file: File, type: FileTypes, pathId: string): Promise<void> => {
    const fileParams = await handleUploadFile(file, type, pathId);

    upsertDocumentOfOrder({
      variables: {
        data: {
          path: fileParams.path,
          domain: fileParams.domain!,
          type: documentType,
          userBusinessProfile: realm as unknown as BusinessProfile,
          orderId,
          orderReferenceId,
        },
      },
    });
  };

  return {
    handleFileChange,
    isDocumentUploadLoading,
    documentData: data,
    reset,
  };
}
